import { useCallback, useEffect, useRef, useState } from 'react';
import {
  actionId,
  BINDABLE_ACTIONS,
  boundCode,
  captureKey,
  inputLabel,
  rebind,
  type BoundAction,
  type InputCode,
} from '../../game/input/bindings';
import { useSettingsStore } from '../../store/settingsStore';
import { SLOT_ROLES } from '../hud/icons';
import { useEscapeLayer } from '../useEscapeLayer';
import { TouchOptions } from './TouchOptions';

const MOVE_ARROWS = { up: '↑', left: '←', down: '↓', right: '→' } as const;

function actionLabel(a: BoundAction): string {
  switch (a.kind) {
    case 'move':
      return `Move ${MOVE_ARROWS[a.dir]}`;
    case 'cast':
      return `Spell · ${SLOT_ROLES[a.slot]}`;
    case 'racial':
      return 'Racial';
    case 'scoreboard':
      return 'Scoreboard';
    case 'mute':
      return 'Mute';
    case 'freeCam':
      return 'Free cam (spectating)';
  }
}

/**
 * Options → Controls: quick-cast and the key bindings editor (DROW-173). Clicking a row captures
 * the next key or mouse button pressed on that row; Esc cancels. A clash swaps the other action
 * onto the old input (mute and the racial go unbound instead) and flashes its row.
 */
export function ControlsTab() {
  const quickCast = useSettingsStore((s) => s.quickCast);
  const setQuickCast = useSettingsStore((s) => s.setQuickCast);
  const keybinds = useSettingsStore((s) => s.keybinds);
  const setKeybinds = useSettingsStore((s) => s.setKeybinds);
  const resetKeybinds = useSettingsStore((s) => s.resetKeybinds);
  const [capturing, setCapturing] = useState<BoundAction | null>(null);
  const [notice, setNotice] = useState('');
  /** The row a clash changed; `n` bumps so the same row re-flashes. */
  const [flash, setFlash] = useState<{ id: string; n: number } | null>(null);
  const captureRow = useRef<HTMLTableRowElement | null>(null);
  /** The click that follows an LMB capture must not start a new capture. */
  const swallowClick = useRef(false);
  /** The release of a captured key must not click the focused row button (Space, Enter). */
  const swallowKeyUp = useRef<InputCode | null>(null);

  const cancel = useCallback(() => setCapturing(null), []);
  // Esc cancels the capture, not Options: this layer sits on top of the modal's while capturing.
  useEscapeLayer(cancel, capturing !== null);

  const commit = useCallback(
    (action: BoundAction, code: InputCode) => {
      setCapturing(null);
      const before = useSettingsStore.getState().keybinds;
      const next = rebind(before, action, code);
      if (next === before) {
        const holder = BINDABLE_ACTIONS.find((a) => boundCode(before, a) === code);
        setNotice(
          holder && actionId(holder) !== actionId(action)
            ? `${inputLabel(code)} stays on ${actionLabel(holder)}: it needs a key, and ${actionLabel(action)} has none to swap.`
            : '',
        );
        return;
      }
      setKeybinds(next);
      const moved = BINDABLE_ACTIONS.find(
        (a) => actionId(a) !== actionId(action) && boundCode(before, a) !== boundCode(next, a),
      );
      if (!moved) return setNotice('');
      const to = boundCode(next, moved);
      setNotice(
        to
          ? `${actionLabel(moved)} moved to ${inputLabel(to)}.`
          : `${actionLabel(moved)} is now unbound.`,
      );
      setFlash((f) => ({ id: actionId(moved), n: (f?.n ?? 0) + 1 }));
    },
    [setKeybinds],
  );

  // While capturing, the next key goes to the editor before anything else sees it (the tab
  // arrows, the game). Esc is left alone for the Esc layer stack.
  useEffect(() => {
    if (!capturing) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return;
      // Tab would move focus and Space scroll or click; nothing should act on this key.
      e.preventDefault();
      e.stopPropagation();
      if (e.repeat) return;
      const result = captureKey(e);
      if ('rejected' in result) return setNotice(result.rejected);
      swallowKeyUp.current = result.code;
      commit(capturing, result.code);
    };
    // A press anywhere but the capturing row cancels; on the row it is the binding.
    const onMouseDown = (e: MouseEvent) => {
      if (!(e.target instanceof Node && captureRow.current?.contains(e.target))) cancel();
    };
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('mousedown', onMouseDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('mousedown', onMouseDown, true);
    };
  }, [capturing, cancel, commit]);

  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== swallowKeyUp.current) return;
      swallowKeyUp.current = null;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('keyup', onKeyUp, true);
    return () => window.removeEventListener('keyup', onKeyUp, true);
  }, []);

  const reset = () => {
    setCapturing(null);
    setNotice('');
    resetKeybinds();
  };

  return (
    <>
      <TouchOptions />
      <label className="option-check">
        <input
          type="checkbox"
          checked={quickCast}
          onChange={(e) => setQuickCast(e.target.checked)}
        />
        <span>
          Quick-cast
          <span className="muted option-hint">
            Cast instantly at the cursor; off = press the key, then click.
          </span>
        </span>
      </label>
      <h3 className="option-heading">Key bindings</h3>
      <p className="muted option-hint">
        Click a binding, then press a key or mouse button on it. Esc cancels.
      </p>
      <table className="keybind-list">
        <tbody>
          {BINDABLE_ACTIONS.map((action) => {
            const id = actionId(action);
            const label = actionLabel(action);
            const code = boundCode(keybinds, action);
            const active = capturing !== null && actionId(capturing) === id;
            return (
              <tr
                key={id}
                ref={active ? captureRow : undefined}
                className={active ? 'capturing' : undefined}
                onClick={() => {
                  if (swallowClick.current) {
                    swallowClick.current = false;
                    return;
                  }
                  setNotice('');
                  setCapturing(active ? null : action);
                }}
                onMouseDown={(e) => {
                  if (!active) return;
                  // Middle-click autoscroll, back/forward: the press is the binding, nothing else.
                  e.preventDefault();
                  swallowClick.current = e.button === 0;
                  commit(action, `Mouse${e.button}`);
                }}
                onMouseUp={(e) => {
                  if (e.button > 2) e.preventDefault();
                }}
                onAuxClick={(e) => e.preventDefault()}
                // RMB is bindable: its menu never opens over the rows.
                onContextMenu={(e) => e.preventDefault()}
              >
                <th scope="row">{label}</th>
                <td>
                  <button
                    type="button"
                    className="keybind-button"
                    aria-label={`${label}: ${code ? inputLabel(code) : 'unbound'}`}
                    aria-pressed={active}
                  >
                    {active ? (
                      <span className="keybind-prompt">Press a key…</span>
                    ) : code ? (
                      <kbd
                        key={flash?.id === id ? flash.n : 0}
                        className={flash?.id === id ? 'flash' : undefined}
                      >
                        {inputLabel(code)}
                      </kbd>
                    ) : (
                      <span
                        key={flash?.id === id ? flash.n : 0}
                        className={flash?.id === id ? 'muted flash' : 'muted'}
                      >
                        Unbound
                      </span>
                    )}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="keybind-notice" role="status">
        {notice}
      </p>
      <button type="button" onClick={reset}>
        Reset to defaults
      </button>
    </>
  );
}
